import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface Unit {
  id: string
  number: string
  building?: string | null
}

interface UnitComboboxProps {
  units: Unit[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
}

export function UnitCombobox({
  units,
  value,
  onChange,
  placeholder = "Digite o número da unidade...",
  disabled = false
}: UnitComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  // Encontrar unidade selecionada
  const selectedUnit = units.find((unit) => unit.id === value)

  // Filtrar e ordenar unidades
  const filteredUnits = React.useMemo(() => {
    if (!search) return units

    const searchLower = search.toLowerCase()
    return units.filter((unit) => {
      const numberMatch = unit.number.toLowerCase().includes(searchLower)
      const buildingMatch = unit.building?.toLowerCase().includes(searchLower)
      return numberMatch || buildingMatch
    })
  }, [units, search])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between h-10"
        >
          {selectedUnit ? (
            <span>
              {selectedUnit.building ? `${selectedUnit.building} - ` : ""}
              Apt {selectedUnit.number}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[--radix-popover-trigger-width] p-0" 
        align="start"
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={8}
      >
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Digite número ou bloco..." 
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[40vh] md:max-h-[300px]">
            <CommandEmpty>Nenhuma unidade encontrada</CommandEmpty>
            <CommandGroup>
              {filteredUnits.map((unit) => (
                <CommandItem
                  key={unit.id}
                  value={unit.id}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? "" : currentValue)
                    setOpen(false)
                    setSearch("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === unit.id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {unit.building ? `${unit.building} - ` : ""}
                  Apt {unit.number}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
